import React, { useState } from "react";
import Select from "react-select";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API_URL from "../Config";
import { trackLead } from "../utils/metaPixel";
import AssetBackdrop from "./AssetBackdrop";
import PasswordInput from "./PasswordInput";
import "./Registration.css";

const userTypeOptions = [
  { value: "architect", label: "Architect" },
  { value: "interior", label: "Interior Designer" },
  { value: "firm", label: "Firm" },
];

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EMPTY_FORM = {
  name: "",
  phoneNumber: "",
  email: "",
  location: "",
  firmName: "",
  password: "",
  confirmPassword: "",
};

const selectClassNames = {
  control: (state) =>
    `signup-select__control${state.isFocused ? " is-focused" : ""}`,
  valueContainer: () => "signup-select__value",
  placeholder: () => "signup-select__placeholder",
  singleValue: () => "signup-select__single",
  indicatorSeparator: () => "signup-select__separator",
  dropdownIndicator: () => "signup-select__indicator",
  menu: () => "signup-select__menu",
  option: (state) =>
    `signup-select__option${state.isFocused ? " is-focused" : ""}${
      state.isSelected ? " is-selected" : ""
    }`,
};

const RegistrationForm = () => {
  const [userType, setUserType] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "phoneNumber" ? value.replace(/\D/g, "") : value,
    }));
  };

  const handleUserTypeChange = (selectedOption) => {
    setUserType(selectedOption);
    if (selectedOption.value !== "firm") {
      setFormData((prev) => ({ ...prev, firmName: "" }));
    }
  };

  const validateForm = () => {
    const {
      name,
      phoneNumber,
      email,
      location,
      firmName,
      password,
      confirmPassword,
    } = formData;
    if (!userType) return "Please select your role.";
    if (
      !name ||
      !phoneNumber ||
      !email ||
      !location ||
      !password ||
      !confirmPassword
    )
      return "Please fill all fields.";
    if (userType.value === "firm" && !firmName)
      return "Please enter your firm name.";
    if (!EMAIL_PATTERN.test(email.trim()))
      return "Please enter a valid email address.";
    if (phoneNumber.length !== 10)
      return "Phone number should be 10 digits.";
    if (password.length < 8 || !password.trim())
      return "Use a password of at least 8 characters.";
    if (password !== confirmPassword) return "Passwords do not match.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) return toast.error(error);

    const payload = {
      name: formData.name,
      firm_name: formData.firmName || null,
      user_type: userType.value,
      phone_number: formData.phoneNumber,
      email: formData.email.trim(),
      address: formData.location,
      password: formData.password,
    };

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/signup`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      trackLead();
      setSubmittedEmail(payload.email);
      setFormData(EMPTY_FORM);
      setUserType(null);
    } catch (err) {
      console.error("Registration error:", err);
      const message =
        err.response?.data?.message ||
        "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <ToastContainer position="top-center" />
      <AssetBackdrop />

      <div className="signup-card">
        <div className="signup-visual">
          <img src="/crimson-jumig-signup.jpg" alt="" />
          <div className="signup-visual-copy">
            <p className="signup-kicker">Archive Portal</p>
            <h1>Create Portal Access</h1>
            <p>
              Register a designer, architect, or firm account for the JUMIG
              Store asset library.
            </p>
          </div>
        </div>

        <div className="signup-panel">
          <div className="signup-panel-head">
            <h2>{submittedEmail ? "Request Submitted" : "Account Details"}</h2>
            <Link to="/login">Back to login</Link>
          </div>

          {submittedEmail ? (
            <div className="signup-success" role="status">
              <p>
                Thank you for registering. Your request has been sent to our
                team for approval.
              </p>
              <p>
                A confirmation has been sent to <strong>{submittedEmail}</strong>.
                You will receive another email with a sign-in link once your
                account is approved.
              </p>
              <Link to="/login" className="signup-button signup-button-link">
                Return to login
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} noValidate>
                <div className="signup-field">
                  <span className="signup-label" id="signup-role-label">
                    Role
                  </span>
                  <Select
                    unstyled
                    aria-labelledby="signup-role-label"
                    options={userTypeOptions}
                    onChange={handleUserTypeChange}
                    value={userType}
                    placeholder="Select role"
                    isSearchable={false}
                    classNames={selectClassNames}
                  />
                </div>

                {userType && (
                  <div className="signup-grid">
                    {userType.value === "firm" && (
                      <input
                        type="text"
                        name="firmName"
                        value={formData.firmName}
                        onChange={handleChange}
                        placeholder="Firm Name"
                        aria-label="Firm Name"
                        autoComplete="organization"
                      />
                    )}
                    <input
                      className={userType.value === "firm" ? "" : "span-2"}
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Location"
                      aria-label="Location"
                      autoComplete="address-level2"
                    />
                  </div>
                )}

                <div className="signup-grid">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Name"
                    aria-label="Name"
                    autoComplete="name"
                  />
                  <input
                    type="tel"
                    inputMode="numeric"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    aria-label="Phone Number"
                    autoComplete="tel"
                  />
                </div>

                <div className="signup-grid">
                  <input
                    className="span-2"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    aria-label="Email Address"
                    autoComplete="email"
                  />
                </div>

                <div className="signup-grid">
                  <PasswordInput
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    aria-label="Password"
                    autoComplete="new-password"
                  />
                  <PasswordInput
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm Password"
                    aria-label="Confirm Password"
                    autoComplete="new-password"
                  />
                </div>

                <button
                  type="submit"
                  className="signup-button"
                  disabled={submitting}
                >
                  {submitting ? "Submitting request..." : "Request Access"}
                </button>
              </form>

              <p className="signup-login">
                Already have an account? <Link to="/login">Login here</Link>
              </p>
            </>
          )}

          <div className="signup-brand">
            <img src="/logo-jumig-store.png" alt="JUMIG Store" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
